import { useEffect, useMemo, useState } from "react";
import { trainerGrade } from "@cimara/trainer";
import type { World } from "@cimara/world";
import {
  difficultyLabel,
  formatDistance,
  formatDraft,
  formatElapsed,
  formatGrade,
  formatLap,
  formatPower,
  formatSpeed,
} from "../lib/format";
import { formatGap, phaseLabel } from "../race/format";
import { useFps } from "../lib/useFps";
import type { RideHud, RideStore } from "../ride/store";
import type { TrainerController, TrainerStatus } from "../ride/useTrainer";
import type { ConnectionState } from "../net/socket";
import type { RaceRegistration } from "../api/types";
import { RangeSlider } from "./RangeSlider";

interface HudProps {
  store: RideStore;
  world: World;
  simPower: number;
  difficulty: number;
  trainer: TrainerController;
  raceRegistration?: RaceRegistration | null;
  onSimPowerChange(value: number): void;
  onDifficultyChange(value: number): void;
  onEndRide(): void;
}

function connectionLabel(state: ConnectionState): string {
  switch (state) {
    case "connected":
      return "Live";
    case "connecting":
      return "Connecting";
    case "reconnecting":
      return "Reconnecting";
    case "error":
      return "Server error";
    case "closed":
      return "Offline";
    default:
      return "Not connected";
  }
}

function trainerStatusLine(status: TrainerStatus, hasBluetooth: boolean): string {
  if (!hasBluetooth) {
    return "Web Bluetooth is not available here. Sim power still drives the ride — use Chrome or Edge on localhost or HTTPS.";
  }
  switch (status) {
    case "connected":
      return "Trainer connected. Power subscriptions stay live even if control fails.";
    case "connecting":
      return "Pairing… choose your FTMS trainer in the browser dialog.";
    case "reconnecting":
      return "Trainer link lost — reconnecting. Sim power takes over if it stays down.";
    case "unavailable":
      return "Web Bluetooth is not available here. Sim power still drives the ride.";
    case "error":
      return "Trainer connection failed. Sim power remains active.";
    default:
      return "Optional. Pair an FTMS trainer for real power; sim power is the fallback.";
  }
}

function shortStatus(status: TrainerStatus): string {
  switch (status) {
    case "connected":
      return "paired";
    case "connecting":
      return "pairing";
    case "reconnecting":
      return "reconnecting";
    case "unavailable":
      return "sim only";
    case "error":
      return "error";
    default:
      return "optional";
  }
}

export function Hud({
  store,
  world,
  simPower,
  difficulty,
  trainer,
  raceRegistration,
  onSimPowerChange,
  onDifficultyChange,
  onEndRide,
}: HudProps) {
  const [hud, setHud] = useState<RideHud>(() => store.getHud());
  const [riderCount, setRiderCount] = useState(0);
  const [trainerOpen, setTrainerOpen] = useState(false);
  const fps = useFps(1000);

  useEffect(() => {
    const update = (): void => {
      const now = Date.now();
      setHud(store.getHud(now));
      // The HUD needs the same number the renderer sees, not a server count.
      setRiderCount(store.collectRiders(now).length);
    };
    update();
    const timer = window.setInterval(update, 100);
    return () => window.clearInterval(timer);
  }, [store]);

  const effectiveGrade = useMemo(
    () => trainerGrade(hud.grade, difficulty),
    [hud.grade, difficulty],
  );

  const route =
    (hud.race ? world.getRoute(hud.race.routeId) : undefined) ?? world.routes[0];
  const sourceLabel = hud.source === "trainer" ? "Trainer" : "Sim";
  const connectionState = hud.connection;
  const fpsLabel = fps > 0 ? String(Math.round(fps)) : "—";
  const race = hud.race;
  const showRaceHud = race !== null && (race.phase === "racing" || race.phase === "finished");
  const raceLap = race ? Math.max(1, race.lap) : 0;
  const raceLaps = race?.laps ?? 0;

  return (
    <div className="hud" data-testid="hud">
      <header className="hud-top">
        <div className="hud-top__left">
          <div className="hud-brand">
            <span className="hud-brand__mark" />
            CIMARA
          </div>
          <div className="hud-route">
            <span>{route?.name ?? "Phase 0 Circuit"}</span>
            <span className="hud-route__sep">/</span>
            <span>Lap {formatLap(showRaceHud && race ? raceLap : hud.laps + 1)}</span>
          </div>
        </div>

        <div className="hud-top__right">
          <div className="hud-readouts">
            <span className="hud-readout" title="Rolling render rate">
              FPS <strong data-testid="fps">{fpsLabel}</strong>
            </span>
            <span className="hud-readout" title="Riders currently rendered, including you">
              Riders <strong data-testid="rider-count">{riderCount}</strong>
            </span>
          </div>
          <div
            className={`connection-pill connection-pill--${connectionState}`}
            data-testid="connection"
            role="status"
            title={hud.connectionDetail}
          >
            <span className="connection-pill__dot" />
            <span>{connectionLabel(connectionState)}</span>
            {connectionState !== "connected" ? (
              <span className="connection-pill__detail">{hud.connectionDetail}</span>
            ) : null}
          </div>
          <button className="hud-end" type="button" onClick={onEndRide} data-testid="end-ride">
            End ride
          </button>
        </div>
      </header>

      {showRaceHud && race ? (
        <section className="race-hud" data-testid="race-hud" aria-label="Race status">
          <div className="race-hud__stats">
            <div className="race-stat">
              <span className="race-stat__label">Position</span>
              <span className="race-stat__value" data-testid="race-position">
                {race.position > 0 ? race.position : "—"}
              </span>
              <span className="race-stat__unit">/ {race.total || "—"}</span>
            </div>
            <div className="race-stat">
              <span className="race-stat__label">Lap</span>
              <span className="race-stat__value" data-testid="race-lap">
                {raceLap} / {raceLaps || "—"}
              </span>
            </div>
            <div className="race-stat">
              <span className="race-stat__label">Gap</span>
              <span className="race-stat__value race-stat__value--gap" data-testid="race-gap">
                {formatGap(race.gap, race.position)}
              </span>
            </div>
            <div className="race-stat race-stat--phase">
              <span className="race-stat__label">Race</span>
              <span className="race-stat__value race-stat__value--phase">
                {phaseLabel(race.phase)}
              </span>
              {raceRegistration ? (
                <span className="race-stat__unit">
                  Cat {raceRegistration.category} · Pen {raceRegistration.pen}
                </span>
              ) : null}
            </div>
          </div>

          <div className="live-board" data-testid="live-board" aria-label="Live top ten">
            <header className="live-board__head">
              <span>Live top 10</span>
              <span>
                {race.total} {race.total === 1 ? "rider" : "riders"}
              </span>
            </header>
            <ol className="live-board__list">
              {race.leaders.slice(0, 10).map((leader) => (
                <li
                  key={`${leader.riderId}-${leader.position}`}
                  className={`live-board__row ${
                    leader.riderId === hud.riderId ? "live-board__row--self" : ""
                  }`}
                >
                  <span className="live-board__pos">{leader.position}</span>
                  <span className="live-board__name">
                    {leader.name}
                    {leader.isBot ? <span className="live-board__bot">BOT</span> : null}
                  </span>
                  <span className="live-board__gap">{formatGap(leader.gap, leader.position)}</span>
                </li>
              ))}
              {race.leaders.length === 0 ? (
                <li className="live-board__empty">Waiting for the first split…</li>
              ) : null}
            </ol>
          </div>
        </section>
      ) : null}

      <div className="hud-bottom">
        <section className="metrics" aria-label="Ride metrics">
          <div className="metric metric--power">
            <span className="metric__label">
              Power
              <span className={`source-chip source-chip--${hud.source}`}>{sourceLabel}</span>
            </span>
            <span className="metric__value" data-testid="power-value">
              {formatPower(hud.power)}
            </span>
            <span className="metric__unit">W</span>
          </div>

          <div className="metric">
            <span className="metric__label">Speed</span>
            <span className="metric__value" data-testid="speed-value">
              {formatSpeed(hud.speedMps)}
            </span>
            <span className="metric__unit">km/h</span>
          </div>

          <div className="metric metric--grade">
            <span className="metric__label">Grade</span>
            <span className="metric__grade-row">
              <span className="metric__value metric__value--small" data-testid="grade-actual">
                {formatGrade(hud.grade)}
              </span>
              <span className="metric__grade-tag">road</span>
            </span>
            <span className="metric__grade-row metric__grade-row--effective">
              <span className="metric__value metric__value--small" data-testid="grade-effective">
                {formatGrade(effectiveGrade)}
              </span>
              <span className="metric__grade-tag">effective</span>
            </span>
          </div>

          <div className="metric">
            <span className="metric__label">Draft</span>
            <span className="metric__value metric__value--small">{formatDraft(hud.draft)}</span>
            <span className="metric__unit">benefit</span>
          </div>

          <div className="metric">
            <span className="metric__label">Distance</span>
            <span className="metric__value metric__value--small">{formatDistance(hud.distanceM)}</span>
            <span className="metric__unit">km</span>
          </div>

          <div className="metric metric--small">
            <span className="metric__label">Time</span>
            <span className="metric__value metric__value--small">{formatElapsed(hud.elapsedMs)}</span>
            <span className="metric__unit">{hud.cadence} rpm</span>
          </div>
        </section>
      </div>

      <aside
        className={`trainer-panel ${
          trainerOpen ? "trainer-panel--open" : "trainer-panel--collapsed"
        }`}
        aria-label="Trainer and effort controls"
      >
        <button
          type="button"
          className="trainer-toggle"
          data-testid="toggle-trainer-panel"
          aria-expanded={trainerOpen}
          aria-controls="trainer-controls"
          onClick={() => setTrainerOpen((open) => !open)}
        >
          <span className="trainer-toggle__title">
            <span className={`trainer-dot trainer-dot--${trainer.status}`} />
            Trainer
          </span>
          <span className="trainer-toggle__action">{trainerOpen ? "Close" : "Controls"}</span>
        </button>

        {!trainerOpen ? (
          <div className="trainer-summary">
            <span>
              Sim <strong>{Math.round(simPower)} W</strong>
            </span>
            <span>
              <strong>{difficultyLabel(difficulty)}</strong> difficulty
            </span>
          </div>
        ) : null}

        <div className="trainer-panel__body" id="trainer-controls" hidden={!trainerOpen}>
          <div className="trainer-panel__head">
            <div>
              <span className="panel-kicker">Effort</span>
              <h2>Trainer settings</h2>
            </div>
            <span className={`trainer-state trainer-state--${trainer.status}`}>
              {shortStatus(trainer.status)}
            </span>
          </div>

          <button
            className="trainer-connect"
            data-testid="connect-trainer"
            type="button"
            onClick={() => {
              if (trainer.connected) {
                void trainer.disconnect();
              } else {
                void trainer.connect();
              }
            }}
            disabled={trainer.status === "connecting"}
          >
            {trainer.connected
              ? "Disconnect trainer"
              : trainer.status === "connecting"
                ? "Pairing…"
                : "Connect trainer"}
          </button>

          <p className="trainer-note">
            {trainer.info
              ? `${trainer.info.name}${trainer.info.firmware ? ` · firmware ${trainer.info.firmware}` : ""}`
              : trainerStatusLine(trainer.status, trainer.hasBluetooth)}
          </p>

          {trainer.error ? (
            <p className="trainer-error" role="alert">
              {trainer.error}
            </p>
          ) : null}

          <label className="control">
            <span className="control__row">
              <span>Trainer difficulty</span>
              <output data-testid="difficulty-value" className="control__value">
                {difficultyLabel(difficulty)}
              </output>
            </span>
            <RangeSlider
              testId="difficulty"
              min={0}
              max={100}
              step={1}
              value={Math.round(difficulty * 100)}
              onValueChange={(value) => onDifficultyChange(value / 100)}
            />
            <span className="control__hint">
              Scales resistance only. The avatar always rides the actual grade.
            </span>
          </label>

          <label className="control">
            <span className="control__row">
              <span>Sim power</span>
              <output className="control__value">{Math.round(simPower)} W</output>
            </span>
            <RangeSlider
              testId="sim-power"
              min={0}
              max={500}
              step={5}
              value={Math.round(simPower)}
              onValueChange={onSimPowerChange}
            />
            <span className="control__hint">
              Development fallback. The avatar moves from this power with no trainer.
            </span>
          </label>

          <div className="trainer-panel__foot">
            <span>
              Road <strong data-testid="panel-grade-actual">{formatGrade(hud.grade)}</strong>
            </span>
            <span>
              Sent <strong>{formatGrade(effectiveGrade)}</strong>
            </span>
            <span className="trainer-panel__id">
              {hud.riderId === null ? "no rider id" : `rider ${hud.riderId}`}
            </span>
          </div>
        </div>
      </aside>

      {hud.paused ? (
        <div className="paused-banner" role="status">
          Server paused the ride — move the sim power slider or pedal to resume.
        </div>
      ) : null}
    </div>
  );
}
